import { Injectable } from '@angular/core';
import { MapperFactory } from '@products/utils/mappers/mapper-factory';
import { SiteConfigService } from '@services/site-config.service';
import { Logger } from '@utils/logger';
import { Apollo } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import {
  FundDataServiceParams,
  GraphQLFundDataService,
} from './graphql-fund-data.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Attribution,
  AttributionCountry,
  AttributionSector,
} from '@products/models/attribution';
import { MapperParams } from '@products/utils/mappers/type.mapper';
import { AttributionCountriesMapper } from '@products/utils/mappers/attribution-countries.type.mapper';
import { AttributionCountryDTO, AttributionSectorDTO } from '@types';
import { AttributionSectorsMapper } from '@products/utils/mappers/attribution-sectors.type.mapper';

const logger = Logger.getLogger('AttributionMainService');

@Injectable({
  providedIn: 'root',
})
export class AttributionService extends GraphQLFundDataService {
  constructor(
    apollo: Apollo,
    siteConfigService: SiteConfigService,
    private mapperFactory: MapperFactory
  ) {
    super(apollo, siteConfigService);
  }

  public register(
    query: DocumentNode,
    fundDataServiceParams: FundDataServiceParams
  ): Observable<Attribution[]> {
    return super.register(query, this.getVariables(fundDataServiceParams)).pipe(
      map((rawHistoricalData) => {
        const mappedAttribution: Attribution[] = [];

        ['AttributionQuarterly', 'AttributionYearly'].forEach((timeperiod) => {
          const attributionCountriesDTO: AttributionCountryDTO[] = rawHistoricalData
            ?.data[timeperiod]?.attribution?.country
            ? rawHistoricalData.data[timeperiod].attribution.country
            : null;

          const attributionSectorsDTO: AttributionSectorDTO[] = rawHistoricalData
            ?.data[timeperiod]?.attribution?.sector
            ? rawHistoricalData.data[timeperiod].attribution.sector
            : null;

          const mappperParams: MapperParams = {
            config: this.siteConfigService,
          };

          const mappedAttributionCountries: AttributionCountry[] = this.mapperFactory
            .createMapper(AttributionCountriesMapper, mappperParams)
            .toDomain(attributionCountriesDTO);

          const mappedAttributionSectors: AttributionSector[] = this.mapperFactory
            .createMapper(AttributionSectorsMapper, mappperParams)
            .toDomain(attributionSectorsDTO);

          mappedAttribution.push({
            country: mappedAttributionCountries,
            sector: mappedAttributionSectors,
          });
        });

        logger.debug(
          `Attribution mapped data [quarterly, yearly]: `,
          mappedAttribution
        );

        return mappedAttribution;
      })
    );
  }

  /**
   * Return required variables value to graphql query.
   * @param fundDataServiceParams params required for graphql query
   */
  private getVariables(
    fundDataServiceParams: FundDataServiceParams
  ): Record<string, any> {
    return {
      countrycode: this.getCountry(),
      languagecode: this.getLanguage(),
      fundid: fundDataServiceParams.fundId,
      asofdatequarterly: fundDataServiceParams.asofdatequarterly,
      asofdateyearly: fundDataServiceParams.asofdateyearly,
    };
  }
}
